import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const getAuthToken = () => localStorage.getItem('token');

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
apiClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
apiClient.interceptors.response.use(
  response => response,
  error => {
    // Token expiré ou invalide → déconnexion automatique + redirect login
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/riot-login';
      return Promise.reject(error);
    }
    if (error.response?.data?.error) {
      error.message = error.response.data.error;
    } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      error.message = error.response.data.errors.map(e => e.msg || e.message).join(', ');
    } else if (error.message === 'Network Error') {
      error.message = 'Impossible de se connecter au serveur (vérifier que le backend est en cours d\'exécution)';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  // Authentication - Classic
  register: (username, email, password, displayName) =>
    apiClient.post('/auth/register', { username, email, password, displayName }),
  
  login: (email, password) =>
    apiClient.post('/auth/login', { email, password }),

  // Authentication - Riot
  riotLogin: (riotId, tagLine, region = 'euw1', email = null) =>
    apiClient.post('/auth/riot-login', { riotId, tagLine, region, ...(email ? { email } : {}) }),
  
  getCurrentUser: () =>
    apiClient.get('/auth/me'),

  linkRiot: (riotId, tagLine, region = 'euw1') =>
    apiClient.post('/auth/link-riot', { riotId, tagLine, region }),

  getRiotStats: () =>
    apiClient.get('/auth/stats'),
  
  updateProfile: (updates) =>
    apiClient.put('/auth/profile', updates),
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Team Management
  getAllTeams: () =>
    apiClient.get('/team-management'),

  createTeam: (name, tag, logo = null) =>
    apiClient.post('/team-management', { name, tag, logo }),

  getMyTeams: () =>
    apiClient.get('/team-management/my-teams'),

  getTeam: (id) =>
    apiClient.get(`/team-management/${id}`),

  getTeamTournaments: (id) =>
    apiClient.get(`/team-management/${id}/tournaments`),

  uploadTeamLogo: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.post('/team-management/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  updateTeam: (id, updates) =>
    apiClient.put(`/team-management/${id}`, updates),
  
  deleteTeam: (id) =>
    apiClient.delete(`/team-management/${id}`),
  
  inviteToTeam: (teamId, value) => {
    const isEmail = value.includes('@');
    return apiClient.post(`/team-management/${teamId}/invite`,
      isEmail ? { email: value } : { username: value }
    );
  },
  
  getPendingInvitations: () =>
    apiClient.get('/team-management/invitations/pending'),
  
  getInvitationPreview: (token) =>
    apiClient.get(`/team-management/invitations/${token}/preview`),

  acceptInvitation: (token) =>
    apiClient.post(`/team-management/invitations/${token}/accept`),

  declineInvitation: (token) =>
    apiClient.post(`/team-management/invitations/${token}/decline`)
};

// Admin API
export const adminAPI = {
  // Users management
  getUsers: () =>
    apiClient.get('/auth/admin/users'),

  makeAdmin: (userId) =>
    apiClient.post(`/auth/admin/make-admin/${userId}`),

  removeAdmin: (userId) =>
    apiClient.post(`/auth/admin/remove-admin/${userId}`),

  getUserDetail: (userId) =>
    apiClient.get(`/auth/admin/users/${userId}`),

  banUser: (userId, { reason, duration, syncDiscord }) =>
    apiClient.post(`/auth/admin/ban/${userId}`, { reason, duration, syncDiscord }),

  unbanUser: (userId, { syncDiscord }) =>
    apiClient.post(`/auth/admin/unban/${userId}`, { syncDiscord }),

  // Tournaments management
  getTournaments: () =>
    apiClient.get('/tournaments'),

  getTournament: (id) =>
    apiClient.get(`/tournaments/${id}`),

  joinTournament: (id, teamId) =>
    apiClient.post(`/tournaments/${id}/join`, { teamId }),

  createTournament: (data) =>
    apiClient.post('/tournaments', data),

  updateTournament: (id, data) =>
    apiClient.put(`/tournaments/${id}`, data),

  deleteTournament: (id) =>
    apiClient.delete(`/tournaments/${id}`),

  generateBracket: (id) =>
    apiClient.post(`/tournaments/${id}/generate-bracket`),

  startTournament: (id) =>
    apiClient.post(`/tournaments/${id}/start`),

  generateTournamentCodes: (id) =>
    apiClient.post(`/tournaments/${id}/generate-codes`),

  uploadTournamentImage: (file, folder = 'logos') => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', folder);
    return apiClient.post('/tournaments/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Gestion équipes dans un tournoi (admin)
  getTournamentTeams: (tournamentId) =>
    apiClient.get(`/tournaments/${tournamentId}/teams`),

  adminAddTeam: (tournamentId, teamId) =>
    apiClient.post(`/tournaments/${tournamentId}/teams`, { teamId }),

  adminRemoveTeam: (tournamentId, teamId) =>
    apiClient.delete(`/tournaments/${tournamentId}/teams/${teamId}`),

  // Liste globale des équipes (pour le sélecteur)
  getAllTeams: (search = '') =>
    apiClient.get(`/teams${search ? `?search=${encodeURIComponent(search)}` : ''}`),
};

export default apiClient;
